import { useEffect, useState } from "react";
import { Lock, ArrowRight, ChartLineUp } from "@phosphor-icons/react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, ReferenceArea,
} from "recharts";
import api from "../lib/api";

// Stable inline-style + Recharts config objects (hoisted to avoid re-allocations on every render)
const CHART_MARGIN = { top: 6, right: 14, left: 6, bottom: 6 };
const AXIS_TICK_STYLE = { fontSize: 10, fontFamily: "monospace", fill: "#71717a" };
const TOOLTIP_CURSOR = { stroke: "#09090b", strokeDasharray: "2 2" };
const TOOLTIP_CONTENT_STYLE = {
  background: "#09090b", border: 0, color: "#fff",
  fontFamily: "monospace", fontSize: 11,
};
const CHART_OUTER_STYLE = { height: 220 };
const REF_AREA_FILL = "#09090b";
const REF_AREA_OPACITY = 0.06;

/**
 * 7-day cash-forecast TEASER for the public Cash Flow Health Check funnel.
 * Renders a small chart with the first N days visible + the rest blurred behind
 * a paywall CTA → "Start free trial for the full 90-day forecast".
 * Mounts only when `shareId` is provided (i.e. lead is captured).
 */
export default function CashForecastTeaser({ shareId, horizonDays = 90, teaserDays = 7 }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!shareId) return;
    api.get(`/public/cash-forecast/${shareId}?days=${horizonDays}&teaser_days=${teaserDays}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.status === 404 ? "Report not found." : "Forecast unavailable."));
  }, [shareId, horizonDays, teaserDays]);

  if (error) {
    return (
      <div className="border border-zinc-200 p-4 text-xs text-zinc-500 font-mono" data-testid="forecast-error">
        / forecast: {error}
      </div>
    );
  }
  if (!data) {
    return (
      <div className="border border-zinc-200 bg-zinc-50 p-8 text-center font-mono text-[11px] tracking-[0.2em] text-zinc-400" data-testid="forecast-loading">
        BUILDING_90_DAY_PROJECTION…
      </div>
    );
  }

  const fmt$ = (n) => `$${Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
  const teaserDay = data.teaser_days;

  // For the chart we project the LOCKED region using the linear model so that the
  // blurred preview visually communicates the direction of the curve, but we never
  // expose exact figures (Tooltip is disabled on locked days).
  const chartPoints = data.points.map((p, i) => {
    const projected = data.starting_cash + data.daily_delta * i;
    return { day: p.day, cash: p.locked ? Math.round(projected) : p.cash, locked: p.locked };
  });

  const burnNegative = data.daily_delta < 0;
  const finalCashProjected = data.starting_cash + data.daily_delta * data.horizon_days;
  const lineColor = burnNegative ? "#D0021B" : "#16a34a";

  return (
    <div className="border-t border-zinc-950 bg-white" data-testid="forecast-teaser">
      <div className="border-b border-zinc-200 px-4 py-2.5 font-mono text-[11px] tracking-[0.2em] uppercase flex items-center justify-between bg-zinc-50">
        <span><ChartLineUp size={12} className="inline mr-2" /> {data.horizon_days}-day cash forecast · day-by-day</span>
        <span className="text-[10px] text-zinc-500 inline-flex items-center gap-1">
          <Lock size={10} weight="fill" /> PRO PREVIEW
        </span>
      </div>

      <div className="px-4 pt-4 grid grid-cols-3 gap-3 text-center">
        <div className="border-r border-zinc-200 pr-3">
          <div className="text-[9px] font-mono tracking-[0.15em] text-zinc-400">STARTING_CASH</div>
          <div className="font-display font-black text-lg tnum mt-1">{fmt$(data.starting_cash)}</div>
        </div>
        <div className="border-r border-zinc-200 pr-3">
          <div className="text-[9px] font-mono tracking-[0.15em] text-zinc-400">DAILY_DELTA</div>
          <div className={`font-display font-black text-lg tnum mt-1 ${burnNegative ? "text-[#D0021B]" : "text-emerald-700"}`}>
            {data.daily_delta >= 0 ? "+" : ""}{fmt$(data.daily_delta)}
          </div>
        </div>
        <div>
          <div className="text-[9px] font-mono tracking-[0.15em] text-zinc-400">{data.breakeven_day ? "ZERO_CASH_DAY" : `DAY_${data.horizon_days}_PROJ`}</div>
          <div className={`font-display font-black text-lg tnum mt-1 ${data.breakeven_day || finalCashProjected < 0 ? "text-[#D0021B]" : "text-zinc-950"}`}>
            {data.breakeven_day ? `D+${data.breakeven_day}` : fmt$(finalCashProjected)}
          </div>
        </div>
      </div>

      <div className="relative px-2 pt-4 pb-2" style={CHART_OUTER_STYLE}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartPoints} margin={CHART_MARGIN}>
            <XAxis dataKey="day" tick={AXIS_TICK_STYLE}
                   tickFormatter={(d) => `D${d}`} interval={Math.max(1, Math.round(data.horizon_days / 8))} />
            <YAxis tick={AXIS_TICK_STYLE}
                   width={56} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
            <Tooltip
              cursor={TOOLTIP_CURSOR}
              contentStyle={TOOLTIP_CONTENT_STYLE}
              formatter={(v, _n, item) => item.payload?.locked ? ["LOCKED · upgrade to view", "cash"] : [fmt$(v), "cash"]}
              labelFormatter={(d) => `Day +${d}`}
            />
            <ReferenceArea x1={teaserDay} x2={data.horizon_days} y1={0} fill={REF_AREA_FILL} fillOpacity={REF_AREA_OPACITY} />
            <Line type="monotone" dataKey="cash" stroke={lineColor} strokeWidth={2.5} dot={false} isAnimationActive={true} />
          </LineChart>
        </ResponsiveContainer>

        {/* Blurred overlay over locked region */}
        <div className="pointer-events-none absolute inset-y-0 right-2 flex items-center justify-center"
             style={{ left: `calc(${(teaserDay / data.horizon_days) * 100}% + 56px)` }}>
          <div className="text-center bg-white/70 backdrop-blur-[3px] px-4 py-3 border border-zinc-300 shadow-sm">
            <Lock size={14} weight="fill" className="inline text-[#FF3E00]" />
            <div className="font-mono text-[10px] tracking-[0.18em] text-zinc-700 mt-1.5">DAYS {teaserDay + 1}–{data.horizon_days} LOCKED</div>
          </div>
        </div>
      </div>

      <div className="border-t border-zinc-950 bg-zinc-950 text-white px-5 py-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="font-display font-black text-base tracking-tight">
            See your cash, every day, for the next {data.horizon_days} days.
          </div>
          <div className="text-[11px] text-zinc-400 mt-0.5">
            Plus AR/AP scenarios, alerts before you go negative, and an AI CFO who explains every dip.
          </div>
        </div>
        <a href="/register" data-testid="forecast-trial-cta"
           className="bg-[#FF3E00] text-white font-semibold text-xs px-5 py-2.5 hover:bg-white hover:text-[#FF3E00] transition-colors inline-flex items-center gap-2">
          START 14-DAY FREE TRIAL <ArrowRight size={12} weight="bold" />
        </a>
      </div>
    </div>
  );
}
